/**
 * `arch/navTree` — the workspace's navigable shape, for a host that renders
 * navigation with its OWN widgets (a VS Code activitybar tree, an IntelliJ
 * tool window) rather than inside the diagram.
 *
 * The server answers it because the server is already holding a warm,
 * incrementally-updated model per package. The alternative — the host
 * compiling its own copy — means a second full parse of the workspace on every
 * keystroke, for data the server has sitting in memory.
 *
 * The projection itself is `buildNavTree` in viewer-core: pure, shared, and
 * tested there. This file is only the protocol shell around it.
 */
import { buildNavTree, type NavTree } from "@archlang/viewer-core";
import type { PackageRouter } from "../workspace/package-router.js";

/** Narrow the answer to one package root; omit for every package. */
export interface NavTreeParams {
  readonly root?: string;
}

export interface NavTreePackage {
  /** Directory URI of the package root — the address a host binds a tab to. */
  readonly root: string;
  /** Manifest `package:` name, else the root's folder name. */
  readonly name: string;
  readonly tree: NavTree;
}

export interface NavTreeResult {
  readonly packages: readonly NavTreePackage[];
}

/** Sent whenever a rebuild may have changed a tree, so a host can invalidate
 *  rather than poll. Deliberately payload-free: the host re-requests what it
 *  currently shows, which is cheaper than diffing on the wire. */
export const MODEL_CHANGED_NOTIFICATION = "arch/modelChanged";
export const NAV_TREE_REQUEST = "arch/navTree";

function nameOf(root: {
  readonly uri: string;
  readonly name?: readonly string[];
}): string {
  if (root.name && root.name.length > 0) return root.name.join(".");
  return root.uri.split("/").filter(Boolean).pop() ?? root.uri;
}

export function provideNavTree(
  routers: readonly PackageRouter[],
  params: NavTreeParams = {},
): NavTreeResult {
  const packages: NavTreePackage[] = [];
  for (const router of routers) {
    for (const { root, services } of router.packages()) {
      if (params.root && root.uri !== params.root) continue;
      packages.push({
        root: root.uri,
        name: nameOf(root),
        // The STUBBED model: a process-first package's modules are all implied
        // rather than declared, and a tree that dropped them would be empty.
        // They arrive flagged `synthesized`, which is what the host renders as
        // a stub row rather than as authored source.
        tree: buildNavTree(services.workspace.stubbedModel()),
      });
    }
  }
  return { packages };
}
